import { usePage } from '@inertiajs/react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Turns a currency field's display value into something `Number()` accepts:
 * strips the thousands spaces and normalises a decimal comma to a dot.
 */
export function normalizeCurrencyInput(value: string): string {
    return value.replace(/\s/g, '').replace(',', '.');
}

/**
 * Formats free typing into a grouped currency string ("1 250 000.50") while
 * keeping it editable — used as an onChange filter, not a final formatter.
 */
export function formatCurrencyInput(value: string | number | null | undefined): string {
    const normalized = String(value ?? '')
        .replace(/\s/g, '')
        .replace(',', '.')
        .replace(/[^\d.]/g, '');

    if (!normalized) {
        return '';
    }

    const [integerPart, ...decimalParts] = normalized.split('.');
    const groupedInteger = integerPart.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const decimalPart = decimalParts.join('');

    return decimalParts.length > 0 ? `${groupedInteger || '0'}.${decimalPart}` : groupedInteger;
}

//format date to yyyy-MM-dd
export function formatDate(date: Date | string): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function formatDateTime(date: Date | string): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export function getYearYY(date: Date | string): string {
    const d = new Date(date);
    return d.getFullYear().toString().slice(-2);
}

export function useCan() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { props } = usePage<any>();
    const abilities: string[] = props?.auth?.abilities ?? [];
    const roles: string[] = props?.auth?.roles ?? [];
    const isAdministrator = roles.includes('Administrator');
    const can = (perm: string) => isAdministrator || abilities.includes(perm);
    const any = (perms: string[]) => perms.some(can);
    const all = (perms: string[]) => perms.every(can);
    // optional: wildcard-ish check
    const canLike = (prefixDot: string) => isAdministrator || abilities.some((a) => a.startsWith(prefixDot.replace('.*', '.')));
    return { can, any, all, canLike, abilities, roles, isAdministrator };
}

// Helper function to check if a date is today
export function isToday(date: Date) {
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dateOnly.getTime() === todayOnly.getTime();
}

export function formatTime(dateString?: string) {
    if (!dateString) return 'No time set';
    const date = new Date(dateString);
    return date.toLocaleTimeString('lt-LT', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
    });
}

/**
 * Format entity ID for display
 * Handles both UUIDs (string) and legacy integer IDs
 * For UUIDs, returns first 8 characters. For integers, returns padded number.
 */
export function formatEntityId(id: string | number, prefix: string = ''): string {
    if (typeof id === 'string') {
        // UUID: use first 8 characters
        const shortId = id.substring(0, 8).toUpperCase();
        return prefix ? `${prefix}-${shortId}` : shortId;
    } else {
        // Legacy integer ID: pad with zeros
        const paddedId = id.toString().padStart(4, '0');
        return prefix ? `${prefix}-${paddedId}` : paddedId;
    }
}

/**
 * Format lead ID for display (L-XXXX or L-XXXXXXXX for UUID)
 */
export function formatLeadId(id: string | number): string {
    return formatEntityId(id, 'L');
}

/**
 * Format project ID for display (P-XXXX or P-XXXXXXXX for UUID)
 */
export function formatProjectId(id: string | number): string {
    return formatEntityId(id, 'P');
}

/**
 * Format work order ID for display (WO-XXXX or WO-XXXXXXXX for UUID)
 */
export function formatWorkOrderId(id: string | number): string {
    return formatEntityId(id, 'WO');
}

export function formatDateForDisplay(date: Date | string): string {
    return formatDate(date).replace(/-/g, '.');
}

/**
 * European day-first date, `dd.MM.yyyy` — what people here read and type.
 *
 * Distinct from formatDateForDisplay, which is year-first: that one sorts as a
 * string and suits dense tables, this one suits a field somebody fills in.
 */
export function formatDateEuropean(date: Date | string): string {
    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
        return '';
    }

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');

    return `${day}.${month}.${d.getFullYear()}`;
}

/**
 * Parse a European `dd.MM.yyyy` (or `dd-MM-yyyy`, `dd/MM/yyyy`) date.
 *
 * Returns null for anything else, including ISO — `new Date()` already handles
 * that, and guessing between the two formats is how 03.04 becomes the wrong month.
 */
export function parseDateEuropean(value: string): Date | null {
    const match = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/.exec(value.trim());

    if (!match) {
        return null;
    }

    const [, day, month, year] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));

    // Rejects 31.02.2026, which the Date constructor would roll into March.
    if (parsed.getDate() !== Number(day) || parsed.getMonth() !== Number(month) - 1) {
        return null;
    }

    return parsed;
}

/**
 * Logger helper that suppresses console.log in production
 * Only logs when APP_ENV is not 'production' or when in development mode
 */
export const logger = {
    log: (...args: unknown[]): void => {
        const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;
        if (!isProduction) {
            console.log(...args);
        }
    },
    warn: (...args: unknown[]): void => {
        const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;
        if (!isProduction) {
            console.warn(...args);
        }
    },
    error: (...args: unknown[]): void => {
        // Always log errors, even in production
        console.error(...args);
    },
    info: (...args: unknown[]): void => {
        const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;
        if (!isProduction) {
            console.info(...args);
        }
    },
};
